'use client';
import { Editor } from '@tiptap/react';
import { IFile } from '@ui/hooks/file/types';
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Image,
  Link,
  Instagram,
  Youtube as YoutubeIcon,
  MoreHorizontal,
  Twitter,
  Video as VideoIcon,
} from 'lucide-react';
import {
  Toggle,
  Separator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/components/ui';
import { InputFileWithTrigger } from '@ui/components/moleculas';
import { twMerge } from 'tailwind-merge';
import { EmailToggle } from './send-email-toggle';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@ui/components/ui/dialog';
import { Input, Button } from '@ui/components';
import { useState, useEffect } from 'react';

interface RichTextEditorToolbarProps {
  editor: Editor;
  exceededFileLimit: boolean;
  files: IFile[];
  maxFiles: number;
  hideFileUpload?: boolean;
  minimized?: boolean;
  showLabel?: boolean;
  onSelectFiles?: (fileList: FileList) => void;
  sendEmail?: boolean;
  onToggleSendEmail?: (value: boolean) => void;
  onLinkClick?: () => void;
  onInstagramClick?: () => void;
  onYoutubeClick?: () => void;
  onTwitterClick?: () => void;
  onVideoClick?: () => void;
  allowVideos?: boolean;
}

const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-@./?%&=]*)?$/;
const instagramUrlPattern = /^https:\/\/[^\s]*instagram\.com[^\s]*$/;
const youtubeUrlPattern = /^https:\/\/[^\s]*(youtube\.com|youtu\.be)[^\s]*$/;
const twitterUrlPattern =
  /^https?:\/\/(www\.)?x\.com\/([a-zA-Z0-9_]{1,15})(\/status\/(\d+))?(\/\S*)?$/;

export const RichTextEditorToolbarList = (props: RichTextEditorToolbarProps) => {
  const toolbars = [
    !props.minimized ? (
      <Toggle
        key="toolbar-bold"
        className={twMerge(
          'flex flex-row items-center justify-start',
          props.minimized ? 'hidden' : '',
        )}
        size="sm"
        pressed={props.editor.isActive('bold')}
        onPressedChange={() => props.editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
        {props.showLabel && <span className="ml-4">Bold</span>}
      </Toggle>
    ) : null,
    !props.minimized ? (
      <Toggle
        key="toolbar-italic"
        className={twMerge(
          'flex flex-row items-center justify-start',
          props.minimized ? 'hidden' : '',
        )}
        size="sm"
        pressed={props.editor.isActive('italic')}
        onPressedChange={() => props.editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
        {props.showLabel && <span className="ml-4">Italic</span>}
      </Toggle>
    ) : null,
    !props.minimized ? (
      <Toggle
        key="toolbar-strikethrough"
        className={twMerge(
          'flex flex-row items-center justify-start',
          props.minimized ? 'hidden' : '',
        )}
        size="sm"
        pressed={props.editor.isActive('strike')}
        onPressedChange={() => props.editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
        {props.showLabel && <span className="ml-4">Strikethrough</span>}
      </Toggle>
    ) : null,
    !props.minimized ? (
      <Separator
        key="toolbar-Separator"
        orientation="vertical"
        className="h-8 w-[1px] hidden md:block"
      />
    ) : null,
    <Toggle
      key="toolbar-bulletList"
      className={twMerge(
        'flex flex-row items-center justify-start',
        props.minimized ? 'hidden' : '',
      )}
      size="sm"
      pressed={props.editor.isActive('bulletList')}
      onPressedChange={() => props.editor.chain().focus().toggleBulletList().run()}>
      <List className="h-4 w-4" />
      {props.showLabel && <span className="ml-4">BulletList</span>}
    </Toggle>,
    <Toggle
      key="toolbar-orderedList"
      className={twMerge(
        'flex flex-row items-center justify-start',
        props.minimized ? 'hidden' : '',
      )}
      size="sm"
      pressed={props.editor.isActive('orderedList')}
      onPressedChange={() => props.editor.chain().focus().toggleOrderedList().run()}>
      <ListOrdered className="h-4 w-4" />
      {props.showLabel && <span className="ml-4">Ordered List</span>}
    </Toggle>,
    <Toggle
      key="toolbar-link"
      className={twMerge(
        'flex flex-row items-center justify-start',
        props.minimized ? 'hidden' : '',
      )}
      size="sm"
      pressed={props.editor.isActive('link')}
      onPressedChange={() => props.onLinkClick?.()}>
      <Link className="h-4 w-4" />
      {props.showLabel && <span className="ml-4">Link</span>}
    </Toggle>,
    <DropdownMenu key="toolbar-more">
      <DropdownMenuTrigger asChild>
        <Toggle
          className={twMerge(
            'flex flex-row items-center justify-start',
            props.minimized ? 'hidden' : '',
          )}
          size="sm"
          pressed={false}
          onPressedChange={() => {}}>
          <MoreHorizontal className="h-4 w-4" />
          {props.showLabel && <span className="ml-4">More</span>}
        </Toggle>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {/* <DropdownMenuItem onClick={() => props.onVideoClick?.()}>
          <VideoIcon className="h-4 w-4 mr-2" />
          Upload Video
        </DropdownMenuItem> */}
        <DropdownMenuItem onClick={() => props.onInstagramClick?.()}>
          <Instagram className="h-4 w-4 mr-2" />
          Instagram
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => props.onYoutubeClick?.()}>
          <YoutubeIcon className="h-4 w-4 mr-2" />
          YouTube
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => props.onTwitterClick?.()}>
          <Twitter className="h-4 w-4 mr-2" />
          Twitter (X)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>,
  ];

  if (!props.hideFileUpload) {
    toolbars.push(
      <InputFileWithTrigger
        key="toolbar-Image"
        className="flex h-[32px] items-center"
        maxFiles={props.maxFiles}
        exceededFileLimit={props.files.length >= props.maxFiles}
        onSelectFiles={props.onSelectFiles}
        allowVideos={props.allowVideos ?? false}>
        <Toggle size="sm" pressed={false}>
          <Image className="h-4 w-4" />
          {props.showLabel && <span className="ml-4">Image</span>}
        </Toggle>
      </InputFileWithTrigger>,
    );

    if (!props.minimized && props.onToggleSendEmail) {
      toolbars.push(
        <div className="flex items-center ml-2">
          <EmailToggle
            value={props.sendEmail || false}
            setValue={props.onToggleSendEmail}
            label={'Notify audience'}
          />
        </div>,
      );
    }
  }

  return toolbars.filter(Boolean) as JSX.Element[];
};

export const RichTextEditorLinkDialog = (props: {
  editor: Editor;
  showLinkDialog: boolean;
  onClose: () => void;
}) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    if (props.showLinkDialog) {
      // Get selected text from editor
      const selectedText = props.editor.state.doc.textBetween(
        props.editor.state.selection.from,
        props.editor.state.selection.to,
      );

      // If there's a link at cursor, get its details for editing
      if (props.editor.isActive('link')) {
        const linkAttrs = props.editor.getAttributes('link');
        setLinkUrl(linkAttrs.href || '');
        setLinkText(selectedText || linkAttrs.text || '');
      } else {
        setLinkUrl('');
        setLinkText(selectedText || '');
      }
    }
  }, [props.showLinkDialog, props.editor]);

  const insertLink = () => {
    if (!linkUrl) return;

    const formattedUrl = linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`;
    const hasSelection = !props.editor.state.selection.empty;

    if (linkText && !hasSelection) {
      // If we have text to insert and no selection, insert text and make it a link
      props.editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'text',
            marks: [{ type: 'link', attrs: { href: formattedUrl } }],
            text: linkText,
          },
        ])
        .run();
    } else {
      // If we have a selection, just apply the link to it
      props.editor.chain().focus().setLink({ href: formattedUrl }).run();
    }

    props.onClose();
  };

  const handleLinkDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      if (linkUrl && !linkError) {
        insertLink();
      }
    }
  };

  return (
    <Dialog open={props.showLinkDialog} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-[320px] w-full bg-white shadow">
        <DialogHeader>
          <DialogTitle>Insert Link</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            className="w-full"
            placeholder="Text"
            onKeyDown={handleLinkDialogKeyDown}
          />
          <Input
            value={linkUrl}
            onChange={(e) => {
              const url = e.target.value;
              setLinkUrl(url);
              setLinkError(urlPattern.test(url) ? '' : 'Please enter a valid URL');
            }}
            className="w-full"
            placeholder="Type or paste a link"
            error={linkError}
            onKeyDown={handleLinkDialogKeyDown}
          />
        </div>
        <DialogFooter>
          <Button
            size="small"
            onClick={insertLink}
            disabled={!linkUrl || !!linkError}
            type={'primary'}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RichTextEditorInstagramDialog = (props: {
  editor: Editor;
  showInstagramDialog: boolean;
  onClose: () => void;
}) => {
  const [instagramUrl, setInstagramUrl] = useState('');
  const [instagramError, setInstagramError] = useState('');

  useEffect(() => {
    if (props.showInstagramDialog) {
      setInstagramUrl('');
      setInstagramError('');
    }
  }, [props.showInstagramDialog]);

  const insertInstagram = () => {
    if (!instagramUrl) return;

    if (!instagramUrlPattern.test(instagramUrl)) {
      setInstagramError('Please enter a valid Instagram URL');
      return;
    }

    props.editor
      .chain()
      .focus()
      .insertContent('\n')
      .insertContent({
        type: 'instagram',
        attrs: { src: instagramUrl },
      })
      .run();

    props.onClose();
  };

  const handleInstagramDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      if (instagramUrl && !instagramError) {
        insertInstagram();
      }
    }
  };

  return (
    <Dialog open={props.showInstagramDialog} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-[320px] w-full bg-white shadow">
        <DialogHeader>
          <DialogTitle>Insert Instagram Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={instagramUrl}
            onChange={(e) => {
              const url = e.target.value;
              setInstagramUrl(url);
              setInstagramError(
                instagramUrlPattern.test(url) ? '' : 'Please enter a valid Instagram URL',
              );
            }}
            className="w-full"
            placeholder="Paste Instagram URL here"
            error={instagramError}
            onKeyDown={handleInstagramDialogKeyDown}
          />
        </div>
        <DialogFooter>
          <Button
            size="small"
            onClick={insertInstagram}
            disabled={!instagramUrl || !!instagramError}
            type={'primary'}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RichTextEditorYouTubeDialog = (props: {
  editor: Editor;
  showYouTubeDialog: boolean;
  onClose: () => void;
}) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeError, setYoutubeError] = useState('');

  useEffect(() => {
    if (props.showYouTubeDialog) {
      setYoutubeUrl('');
      setYoutubeError('');
    }
  }, [props.showYouTubeDialog]);

  const insertYoutube = () => {
    if (!youtubeUrl) return;

    if (!youtubeUrlPattern.test(youtubeUrl)) {
      setYoutubeError('Please enter a valid YouTube URL');
      return;
    }

    props.editor
      .chain()
      .focus()
      .insertContent('\n')
      .insertContent({
        type: 'youtube',
        attrs: { src: youtubeUrl },
      })
      .run();

    props.onClose();
  };

  const handleYouTubeDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      if (youtubeUrl && !youtubeError) {
        insertYoutube();
      }
    }
  };

  return (
    <Dialog open={props.showYouTubeDialog} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-[320px] w-full bg-white shadow">
        <DialogHeader>
          <DialogTitle>Insert YouTube Video</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={youtubeUrl}
            onChange={(e) => {
              const url = e.target.value;
              setYoutubeUrl(url);
              setYoutubeError(
                youtubeUrlPattern.test(url) ? '' : 'Please enter a valid YouTube URL',
              );
            }}
            className="w-full"
            placeholder="Paste YouTube URL here"
            error={youtubeError}
            onKeyDown={handleYouTubeDialogKeyDown}
          />
        </div>
        <DialogFooter>
          <Button
            size="small"
            onClick={insertYoutube}
            disabled={!youtubeUrl || !!youtubeError}
            type={'primary'}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RichTextEditorTwitterDialog = (props: {
  editor: Editor;
  showTwitterDialog: boolean;
  onClose: () => void;
}) => {
  const [twitterUrl, setTwitterUrl] = useState('');
  const [twitterError, setTwitterError] = useState('');

  useEffect(() => {
    if (props.showTwitterDialog) {
      setTwitterUrl('');
      setTwitterError('');
    }
  }, [props.showTwitterDialog]);

  const insertTwitter = () => {
    if (!twitterUrl) return;

    if (!twitterUrlPattern.test(twitterUrl)) {
      setTwitterError('Please enter a valid X (Twitter) URL');
      return;
    }

    props.editor
      .chain()
      .focus()
      .insertContent('\n')
      .insertContent({
        type: 'twitter',
        attrs: { src: twitterUrl },
      })
      .run();

    props.onClose();
  };

  const handleTwitterDialogKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      if (twitterUrl && !twitterError) {
        insertTwitter();
      }
    }
  };

  return (
    <Dialog open={props.showTwitterDialog} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-[320px] w-full bg-white shadow">
        <DialogHeader>
          <DialogTitle>Insert Twitter (X) Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            value={twitterUrl}
            onChange={(e) => {
              const url = e.target.value;
              setTwitterUrl(url);
              setTwitterError(
                twitterUrlPattern.test(url) ? '' : 'Please enter a valid X (Twitter) URL',
              );
            }}
            className="w-full"
            placeholder="Paste X (Twitter) URL here"
            error={twitterError}
            onKeyDown={handleTwitterDialogKeyDown}
          />
        </div>
        <DialogFooter>
          <Button
            size="small"
            onClick={insertTwitter}
            disabled={!twitterUrl || !!twitterError}
            type={'primary'}>
            Insert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RichTextEditorVideoDialog = (props: {
  editor: Editor;
  showVideoDialog: boolean;
  onClose: () => void;
  onUploadVideo: (file: File) => Promise<string>;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoError, setVideoError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (props.showVideoDialog) {
      setSelectedFile(null);
      setVideoError('');
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }
  }, [props.showVideoDialog, previewUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      setVideoError('Please select a valid video file (MP4, MOV, or WebM)');
      return;
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setVideoError('Video file size must be less than 100MB');
      return;
    }

    // Validate duration via metadata (<= 120s)
    const objectUrl = URL.createObjectURL(file);
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.src = objectUrl;
    videoEl.onloadedmetadata = () => {
      const duration = videoEl.duration || 0;
      URL.revokeObjectURL(objectUrl);
      if (duration > 120) {
        setVideoError('Video duration must be 2 minutes (120s) or less.');
        setSelectedFile(null);
        setPreviewUrl(null);
        return;
      }

      setSelectedFile(file);
      setVideoError('');
      // Use a single preview URL and make sure to clean it up when dialog closes
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const nextPreview = URL.createObjectURL(file);
      setPreviewUrl(nextPreview);
    };
    videoEl.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setVideoError('Unable to read video metadata. Please try another file.');
    };
  };

  const insertVideo = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      const videoUrl = await props.onUploadVideo(selectedFile);

      props.editor
        .chain()
        .focus()
        .insertContent('\n')
        .insertContent({
          type: 'video',
          attrs: { src: videoUrl },
        })
        .run();

      // Cleanup
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      props.onClose();
    } catch (error) {
      setVideoError('Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={props.showVideoDialog} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-[420px] w-full bg-white shadow">
        <DialogHeader>
          <DialogTitle>Upload Video</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-orange-50 file:text-orange-700
                hover:file:bg-orange-100
                cursor-pointer"
            />
            {videoError && <p className="text-red-500 text-sm">{videoError}</p>}
          </div>

          {previewUrl && (
            <div className="relative w-full rounded-lg overflow-hidden bg-black">
              <video src={previewUrl} controls className="w-full h-auto max-h-60" />
            </div>
          )}

          {selectedFile && (
            <div className="text-sm text-gray-600">
              <p>
                <strong>File:</strong> {selectedFile.name}
              </p>
              <p>
                <strong>Size:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button size="small" onClick={props.onClose} type={'secondary'}>
            Cancel
          </Button>
          <Button
            size="small"
            onClick={insertVideo}
            disabled={!selectedFile || !!videoError || isUploading}
            isLoading={isUploading}
            type={'primary'}>
            {isUploading ? 'Uploading...' : 'Insert Video'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
